// PlanService.js

import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';

// Utilities
import { generateKey } from 'utilities/Keys';

import { collections, db } from 'firebaseConfig';

export const PLAN_STATUS = Object.freeze({
  ACTIVE: 'active',
  ARCHIVED: 'archived',
});

export const PLAN_PRICING_MODEL = Object.freeze({
  FREE: 'free',
  PER_SEAT: 'per_seat',
});

export const PLAN_UPLOAD_OPTIONS = Object.freeze([
  { label: '10 MB', value: 10 * 1024 * 1024 },
  { label: '25 MB', value: 25 * 1024 * 1024 },
  { label: '50 MB', value: 50 * 1024 * 1024 },
  { label: '100 MB', value: 100 * 1024 * 1024 },
  { label: '250 MB', value: 250 * 1024 * 1024 },
  { label: '500 MB', value: 500 * 1024 * 1024 },
]);

export const PLAN_LIVE_DEFAULTS = Object.freeze({
  liveConcurrentRoomsCap: 1,
  liveEgressMinutesMonthlyCap: 0,
  liveEnabled: false,
  liveIdleAutoEndMinutes: 5,
  liveMaxParticipantsPerRoom: 8,
  liveMaxSessionMinutes: 90,
  liveParticipantMinutesMonthlyCap: 0,
  liveRecordingEnabled: false,
});

export const PLAN_USAGE_CREDITS_DEFAULTS = Object.freeze({
  free: 500,
  perSeat: 10000,
  beta: 50000,
});

const DEFAULT_STORAGE_BYTES_PER_SEAT = 2 * 1024 * 1024 * 1024;
const CURRENCY_USD = 'USD';

/**
 * resolvePerSeatField
 *
 * Reads a per-seat plan allocation from the primary or legacy field name.
 */
function resolvePerSeatField(input, primaryKey, legacyKey) {
  const primary = normalizeNullableInteger(input?.[primaryKey]);
  if (primary != null) {
    return primary;
  }

  return normalizeNullableInteger(input?.[legacyKey]);
}

/**
 * normalizeString
 *
 * Normalizes an input value to a trimmed string.
 *
 * @param {unknown} value - Input value to normalize.
 *
 * @returns {string} Trimmed string value.
 */
function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * normalizeNonNegativeInteger
 *
 * Returns a non-negative integer with a fallback when parsing fails.
 *
 * @param {unknown} value - Value to parse.
 * @param {number} fallback - Fallback integer value.
 *
 * @returns {number} Parsed integer or fallback.
 */
function normalizeNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.floor(parsed);
  if (whole < 0) return fallback;
  return whole;
}

/**
 * normalizeNullableInteger
 *
 * Returns a positive integer or null.
 *
 * @param {unknown} value - Value to parse.
 *
 * @returns {number|null} Positive integer or null.
 */
function normalizeNullableInteger(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const whole = Math.floor(parsed);
  if (whole < 1) return null;
  return whole;
}

/**
 * normalizeStatus
 *
 * Ensures status is one of the allowed values.
 *
 * @param {unknown} value - Raw status value.
 *
 * @returns {string} Normalized status value.
 */
function normalizeStatus(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === PLAN_STATUS.ARCHIVED) {
    return PLAN_STATUS.ARCHIVED;
  }
  return PLAN_STATUS.ACTIVE;
}

/**
 * normalizePricingModel
 *
 * Ensures pricingModel is one of the allowed values.
 *
 * @param {unknown} value - Raw pricing model value.
 *
 * @returns {string} Normalized pricing model value.
 */
function normalizePricingModel(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === PLAN_PRICING_MODEL.PER_SEAT) {
    return PLAN_PRICING_MODEL.PER_SEAT;
  }
  return PLAN_PRICING_MODEL.FREE;
}

/**
 * normalizePubliclyVisible
 *
 * Defaults to true when the visibility flag is omitted.
 *
 * @param {unknown} value - Raw visibility value.
 *
 * @returns {boolean} Normalized visibility value.
 */
function normalizePubliclyVisible(value) {
  if (value == null) {
    return true;
  }
  return Boolean(value);
}

/**
 * normalizePositiveIntegerWithFallback
 *
 * Returns a positive integer or the fallback value.
 *
 * @param {unknown} value - Value to parse.
 * @param {number} fallback - Fallback integer value.
 *
 * @returns {number} Positive integer value.
 */
function normalizePositiveIntegerWithFallback(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.floor(parsed);
  if (whole < 1) return fallback;
  return whole;
}

/**
 * normalizePlanKey
 *
 * Returns a generated immutable plan key when no valid key exists.
 *
 * @param {unknown} value - Existing key value.
 *
 * @returns {string} Plan key.
 */
function normalizePlanKey(value) {
  const normalized = normalizeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (normalized) {
    return normalized;
  }
  return `plan_${generateKey(20)}`;
}

/**
 * Returns whether a normalized plan row is a free plan.
 *
 * @param {object} plan - Normalized plan row.
 *
 * @returns {boolean} True when the plan is free-tier.
 */
export function isFreePlan(plan) {
  const pricingModel = normalizePricingModel(plan?.pricingModel);
  const seatPrice = normalizeNonNegativeInteger(plan?.seatPrice, 0);
  return pricingModel === PLAN_PRICING_MODEL.FREE || seatPrice <= 0;
}

/**
 * buildPlanPayload
 *
 * Normalizes a plan object into the persisted Firestore schema.
 *
 * @param {object} input - Raw plan payload.
 * @param {number} input.sort - Sort index.
 *
 * @returns {object} Normalized plan payload.
 */
function buildPlanPayload(input = {}) {
  const key = normalizePlanKey(input.key);
  const pricingModel = normalizePricingModel(input.pricingModel);
  const seatPriceRaw = normalizeNonNegativeInteger(input.seatPrice, 0);
  const seatPrice = pricingModel === PLAN_PRICING_MODEL.FREE ? 0 : seatPriceRaw;
  const stripePriceId = pricingModel === PLAN_PRICING_MODEL.FREE
    ? null
    : normalizeString(input.stripePriceId) || null;
  const apiEnabled = Boolean(input.apiEnabled);
  const apiMonthlyRunLimitPerSeat = apiEnabled
    ? normalizeNonNegativeInteger(
      resolvePerSeatField(input, 'apiMonthlyRunLimitPerSeat', 'apiMonthlyRunLimit'),
      0
    )
    : 0;
  const maxUploadBytes = normalizeNullableInteger(input.maxUploadBytes);
  const liveEnabled = Boolean(input.liveEnabled);
  const liveRecordingEnabled = liveEnabled && Boolean(input.liveRecordingEnabled);
  const storageBytesPerSeat = resolvePerSeatField(
    input,
    'storageBytesPerSeat',
    'pooledStorageBytes'
  ) || DEFAULT_STORAGE_BYTES_PER_SEAT;
  const liveParticipantMinutesPerSeat = liveEnabled
    ? normalizeNonNegativeInteger(
      resolvePerSeatField(
        input,
        'liveParticipantMinutesPerSeat',
        'liveParticipantMinutesMonthlyCap'
      ),
      0
    )
    : 0;
  const liveEgressMinutesPerSeat = liveRecordingEnabled
    ? normalizeNonNegativeInteger(
      resolvePerSeatField(
        input,
        'liveEgressMinutesPerSeat',
        'liveEgressMinutesMonthlyCap'
      ),
      0
    )
    : 0;
  // Per-seat plans may still cap seats; null = unlimited.
  const maxUsers = normalizeNullableInteger(input.maxUsers);
  const includedUsageCreditsPerSeat = normalizeNonNegativeInteger(
    input.includedUsageCreditsPerSeat,
    pricingModel === PLAN_PRICING_MODEL.PER_SEAT
      ? PLAN_USAGE_CREDITS_DEFAULTS.perSeat
      : PLAN_USAGE_CREDITS_DEFAULTS.free
  );
  const aiEnabled = input.aiEnabled !== false;
  const usageMeterEnabled = Boolean(input.usageMeterEnabled);
  // Module / surface flags — omit means legacy default (see entitlements resolver).
  const hasFlag = (name) => Object.prototype.hasOwnProperty.call(input, name);

  return {
    aiEnabled,
    apiEnabled,
    apiMonthlyRunLimit: apiMonthlyRunLimitPerSeat,
    apiMonthlyRunLimitPerSeat,
    appsEnabled: hasFlag('appsEnabled') ? Boolean(input.appsEnabled) : true,
    automationsEnabled: Boolean(input.automationsEnabled),
    bypassTrialLock: Boolean(input.bypassTrialLock),
    channelsEnabled: hasFlag('channelsEnabled') ? Boolean(input.channelsEnabled) : true,
    currency: CURRENCY_USD,
    dashboardsEnabled: hasFlag('dashboardsEnabled') ? Boolean(input.dashboardsEnabled) : true,
    dmsEnabled: hasFlag('dmsEnabled') ? Boolean(input.dmsEnabled) : true,
    formsEnabled: hasFlag('formsEnabled') ? Boolean(input.formsEnabled) : true,
    includedUsageCreditsPerSeat,
    key,
    knowledgeEnabled: hasFlag('knowledgeEnabled') ? Boolean(input.knowledgeEnabled) : true,
    pagesEnabled: hasFlag('pagesEnabled') ? Boolean(input.pagesEnabled) : true,
    liveConcurrentRoomsCap: normalizePositiveIntegerWithFallback(
      input.liveConcurrentRoomsCap,
      PLAN_LIVE_DEFAULTS.liveConcurrentRoomsCap
    ),
    liveEgressMinutesMonthlyCap: liveEgressMinutesPerSeat,
    liveEgressMinutesPerSeat,
    liveEnabled,
    liveIdleAutoEndMinutes: normalizePositiveIntegerWithFallback(
      input.liveIdleAutoEndMinutes,
      PLAN_LIVE_DEFAULTS.liveIdleAutoEndMinutes
    ),
    liveMaxParticipantsPerRoom: normalizePositiveIntegerWithFallback(
      input.liveMaxParticipantsPerRoom,
      PLAN_LIVE_DEFAULTS.liveMaxParticipantsPerRoom
    ),
    liveMaxSessionMinutes: normalizePositiveIntegerWithFallback(
      input.liveMaxSessionMinutes,
      PLAN_LIVE_DEFAULTS.liveMaxSessionMinutes
    ),
    liveParticipantMinutesMonthlyCap: liveParticipantMinutesPerSeat,
    liveParticipantMinutesPerSeat,
    liveRecordingEnabled,
    maxUploadBytes,
    maxUsers,
    pooledStorageBytes: storageBytesPerSeat,
    pricingModel,
    storageBytesPerSeat,
    isDefault: Boolean(input.isDefault),
    publiclyVisible: normalizePubliclyVisible(input.publiclyVisible),
    seatPrice,
    sort: normalizeNonNegativeInteger(input.sort, 0),
    stripePriceId,
    status: normalizeStatus(input.status),
    title: normalizeString(input.title) || 'Untitled Plan',
    usageMeterEnabled,
  };
}

/**
 * cleanPlanRow
 *
 * Normalizes a Firestore plan document snapshot into the UI/service shape.
 *
 * @param {object} docSnap - Firestore document snapshot.
 *
 * @returns {object} Normalized plan row.
 */
function cleanPlanRow(docSnap) {
  const row = docSnap?.data() || {};
  // Document id is canonical for assignment lookups. Never prefer a drifted `key` field.
  const documentKey = normalizeString(docSnap?.id);
  const normalizedRow = buildPlanPayload({
    ...row,
    key: documentKey,
  });

  return {
    ...normalizedRow,
    id: documentKey,
    key: documentKey,
    dateCreated: row.dateCreated || null,
    dateModified: row.dateModified || null,
  };
}

/**
 * Lists all plans from Firestore.
 *
 * @returns {Promise<Array<object>>} Plans sorted by sort, then title, then key.
 */
export async function listPlans() {
  const snapshot = await getDocs(collection(db, collections.plans));

  return snapshot.docs
    .map(cleanPlanRow)
    .sort((left, right) => {
      const sortDiff = left.sort - right.sort;
      if (sortDiff !== 0) return sortDiff;

      const titleSort = left.title.localeCompare(right.title);
      if (titleSort !== 0) return titleSort;

      return left.key.localeCompare(right.key);
    });
}

/**
 * getPlanByKey
 *
 * Reads a single plan document by key.
 *
 * @param {object} input - Read input.
 * @param {string} input.planKey - Plan key.
 *
 * @returns {Promise<object|null>} Normalized plan row or null.
 */
export async function getPlanByKey({ planKey }) {
  const cleanedPlanKey = normalizeString(planKey);
  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  const planSnap = await getDoc(doc(db, collections.plans, cleanedPlanKey));
  if (!planSnap.exists()) {
    return null;
  }

  return cleanPlanRow(planSnap);
}

/**
 * Lists only active publicly visible plans for customer-facing surfaces.
 *
 * @returns {Promise<Array<object>>} Active visible plans sorted by sort, then title, then key.
 */
export async function listActivePlans() {
  const rows = await listPlans();
  return rows.filter((row) => (
    row.status === PLAN_STATUS.ACTIVE
    && (row.publiclyVisible === true || row.isDefault === true)
  ));
}

/**
 * Creates a new plan document in Firestore.
 *
 * @param {object} input - Create input.
 *
 * @returns {Promise<object>} Created plan summary.
 */
export async function createPlan(input = {}) {
  const existingPlans = await listPlans();
  const hasDefault = existingPlans.some((plan) => plan.isDefault === true);
  const shouldForceDefault = existingPlans.length === 0 || !hasDefault;
  const wantsDefault = shouldForceDefault || Boolean(input.isDefault);

  const payload = buildPlanPayload({
    ...input,
    isDefault: wantsDefault,
  });

  if (wantsDefault) {
    return setDefaultPlan({
      plan: payload,
      planKey: payload.key,
    });
  }

  await setDoc(
    doc(db, collections.plans, payload.key),
    {
      ...payload,
      dateCreated: serverTimestamp(),
      dateModified: serverTimestamp(),
    },
    { merge: false }
  );

  return payload;
}

/**
 * Updates a plan document with full normalized field values.
 *
 * @param {object} input - Update input.
 * @param {string} input.planKey - Existing immutable plan key.
 * @param {object} input.plan - Full plan payload to persist.
 *
 * @returns {Promise<void>}
 */
export async function updatePlan({ planKey, plan }) {
  const cleanedPlanKey = normalizeString(planKey);

  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  const payload = buildPlanPayload({
    ...plan,
    key: cleanedPlanKey,
  });

  await updateDoc(doc(db, collections.plans, cleanedPlanKey), {
    ...payload,
    // Strip retired plan flag so stale false values cannot linger.
    collectionsEnabled: deleteField(),
    dateModified: serverTimestamp(),
  });
}

/**
 * Persists a plan as the base default and clears isDefault on all other plans.
 *
 * @param {object} input - Save input.
 * @param {string} input.planKey - Target plan key.
 * @param {object} input.plan - Full normalized plan payload to persist.
 *
 * @returns {Promise<object>} Saved plan payload.
 */
export async function setDefaultPlan({ planKey, plan }) {
  const payload = buildPlanPayload({
    ...plan,
    isDefault: true,
    key: planKey || plan?.key,
  });
  const cleanedPlanKey = normalizeString(payload.key);

  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  if (!isFreePlan(payload)) {
    throw new Error('Only a free plan can be the default plan.');
  }

  if (payload.status !== PLAN_STATUS.ACTIVE) {
    throw new Error('Only an active plan can be the default plan.');
  }

  const snapshot = await getDocs(collection(db, collections.plans));
  const batch = writeBatch(db);
  const targetRef = doc(db, collections.plans, cleanedPlanKey);
  const targetSnap = await getDoc(targetRef);

  snapshot.docs.forEach((planDoc) => {
    const row = planDoc.data() || {};
    const key = normalizeString(row.key || planDoc.id);
    if (key === cleanedPlanKey || row.isDefault !== true) {
      return;
    }

    batch.update(planDoc.ref, {
      dateModified: serverTimestamp(),
      isDefault: false,
    });
  });

  if (targetSnap.exists()) {
    batch.update(targetRef, {
      ...payload,
      collectionsEnabled: deleteField(),
      dateModified: serverTimestamp(),
      isDefault: true,
    });
  } else {
    batch.set(targetRef, {
      ...payload,
      dateCreated: serverTimestamp(),
      dateModified: serverTimestamp(),
      isDefault: true,
    });
  }

  await batch.commit();
  return payload;
}

/**
 * Backfills a default plan when legacy rows have no isDefault flag set.
 *
 * Uses the previous implicit rule: lowest sort among active free publicly visible plans.
 *
 * @returns {Promise<{ updated: boolean, planKey: string|null }>}
 */
export async function backfillMissingDefaultPlan() {
  const rows = await listPlans();
  if (!rows.length || rows.some((plan) => plan.isDefault === true)) {
    return { updated: false, planKey: null };
  }

  const legacyDefault = rows
    .filter((plan) => (
      plan.status === PLAN_STATUS.ACTIVE
      && plan.publiclyVisible === true
      && isFreePlan(plan)
    ))
    .sort((left, right) => {
      const sortDiff = left.sort - right.sort;
      if (sortDiff !== 0) return sortDiff;

      const titleDiff = String(left.title || '').localeCompare(String(right.title || ''));
      if (titleDiff !== 0) return titleDiff;

      return String(left.key || '').localeCompare(String(right.key || ''));
    })[0] || null;

  if (!legacyDefault?.key) {
    return { updated: false, planKey: null };
  }

  await setDefaultPlan({
    plan: legacyDefault,
    planKey: legacyDefault.key,
  });

  return { updated: true, planKey: legacyDefault.key };
}

/**
 * Reorders plans and persists zero-based sort values in batch.
 *
 * @param {Array<string>} orderedPlanKeys - Plan keys in final order.
 *
 * @returns {Promise<void>}
 */
export async function reorderPlans(orderedPlanKeys = []) {
  if (!Array.isArray(orderedPlanKeys) || !orderedPlanKeys.length) {
    return;
  }

  const batch = writeBatch(db);

  orderedPlanKeys.forEach((rawKey, index) => {
    const planKey = normalizeString(rawKey);
    if (!planKey) {
      return;
    }

    batch.update(doc(db, collections.plans, planKey), {
      dateModified: serverTimestamp(),
      sort: index,
    });
  });

  await batch.commit();
}

/**
 * Deletes a plan document. Refuses the default plan and plans still assigned to bases.
 *
 * @param {object} input
 * @param {string} input.planKey - Plan document id.
 * @param {number} [input.assignedBaseCount=0] - Bases still on this plan.
 *
 * @returns {Promise<void>}
 */
export async function deletePlan({ planKey, assignedBaseCount = 0 } = {}) {
  const cleanedPlanKey = normalizeString(planKey);
  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  const planSnap = await getDoc(doc(db, collections.plans, cleanedPlanKey));
  if (!planSnap.exists()) {
    throw new Error('Plan not found.');
  }

  const planRow = cleanPlanRow(planSnap);
  if (planRow.isDefault === true) {
    throw new Error('Cannot delete the default plan. Assign a different default first.');
  }

  const assignedCount = Math.max(0, Math.floor(Number(assignedBaseCount) || 0));
  if (assignedCount > 0) {
    throw new Error(
      `Cannot delete: ${assignedCount} base${assignedCount === 1 ? '' : 's'} still use this plan. Reassign them first.`,
    );
  }

  await deleteDoc(doc(db, collections.plans, cleanedPlanKey));
}
